import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import '../superadmin/DistrictsList.css';

const ReturningStudentCSVUpload = () => {
  const { user } = useAuth();
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    fetchUploadHistory();
  }, []);

  const fetchUploadHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await axios.get(`/api/returning-student-imports/history/${user?.district_id}`);
      setUploadHistory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch upload history:', error);
      setUploadHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]);
    setUploadResult(null);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      setUploadResult({
        success: false,
        error: 'Please select a CSV file'
      });
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('csvFile', csvFile);

      const response = await axios.post(
        `/api/returning-student-imports/upload/${user?.district_id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setUploadResult({
        success: true,
        message: response.data.message,
        studentCount: response.data.studentCount
      });
      
      setCsvFile(null);
      // Reset file input
      const fileInput = document.getElementById('csvFile');
      if (fileInput) fileInput.value = '';
      
      // Refresh upload history
      await fetchUploadHistory();
    } catch (error) {
      console.error('CSV upload error:', error);
      console.error('CSV upload error response:', error.response?.data);
      setUploadResult({
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to upload CSV file'
      });
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="districts-list">
      <div className="page-header">
        <h2>Returning Student CSV Import</h2>
      </div>

      <div style={{
        padding: '1.5rem',
        marginBottom: '2rem',
        background: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '6px'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Upload CSV File</h3>
        <p style={{ marginBottom: '1rem', color: '#6c757d', lineHeight: '1.6' }}>
          Upload a CSV file containing currently enrolled students. This will allow parents to import student data 
          when filling out returning student enrollment forms. This is typically done once per year, per district.
        </p>
        <p style={{ marginBottom: '1rem', color: '#856404', background: '#fff3cd', padding: '0.75rem', borderRadius: '4px', border: '1px solid #ffeaa7' }}>
          <strong>Note:</strong> The CSV should contain columns: Student Legal Last Name, Student Federal ID Number, 
          Guardian Legal First Name, Guardian Home Phone, Guardian Residence Address, etc. The system will match 
          students by last name and last 4 digits of SSN.
        </p>

        <form onSubmit={handleUpload} style={{ marginBottom: '1rem' }}>
          <div className="form-group">
            <label htmlFor="csvFile">CSV File *</label>
            <input
              id="csvFile"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              required
              style={{
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                width: '100%',
                maxWidth: '400px'
              }}
            />
          </div>
          <button
            type="submit"
            className="btn-primary"
            disabled={uploading || !csvFile}
            style={{
              opacity: (uploading || !csvFile) ? 0.6 : 1,
              cursor: (uploading || !csvFile) ? 'not-allowed' : 'pointer'
            }}
          >
            {uploading ? 'Uploading...' : 'Upload CSV'}
          </button>
        </form>

        {uploadResult && (
          <div style={{
            padding: '0.75rem',
            marginTop: '1rem',
            backgroundColor: uploadResult.success ? '#d4edda' : '#f8d7da',
            border: `1px solid ${uploadResult.success ? '#c3e6cb' : '#f5c6cb'}`,
            borderRadius: '4px',
            color: uploadResult.success ? '#155724' : '#721c24'
          }}>
            {uploadResult.success ? (
              <div>
                <strong>✅ Upload Successful:</strong> {uploadResult.message}
                {uploadResult.studentCount !== undefined && (
                  <p style={{ margin: '0.5rem 0 0 0' }}>
                    Found {uploadResult.studentCount} student record(s) in the CSV.
                  </p>
                )}
              </div>
            ) : (
              <div>
                <strong>❌ Upload Failed:</strong> {uploadResult.error}
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <h3 style={{ marginBottom: '1rem' }}>Upload History</h3>
        {loadingHistory ? (
          <p>Loading upload history...</p>
        ) : uploadHistory.length === 0 ? (
          <p style={{ color: '#6c757d', fontStyle: 'italic' }}>No uploads yet.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Uploaded At</th>
                <th>Filename</th>
                <th>Student Count</th>
                <th>Uploaded By</th>
              </tr>
            </thead>
            <tbody>
              {uploadHistory.map((upload) => (
                <tr key={upload.id}>
                  <td>{formatDate(upload.created_at)}</td>
                  <td>{upload.filename || 'N/A'}</td>
                  <td>{upload.student_count || 0}</td>
                  <td>{upload.uploaded_by_username || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ReturningStudentCSVUpload;
